import { useEffect, useState, useCallback } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { apiService, isAbortError, UserProfile } from '../services/api';

export interface AgentAccess {
  competitive: boolean;
  market_research: boolean;
  customer_research: boolean;
}

interface AuthState {
  isLoaded: boolean;
  isSignedIn: boolean;
  userId: string | null;
  credits: number;
  agentAccess: AgentAccess;
  profile: UserProfile;
}

const defaultAgentAccess: AgentAccess = {
  competitive: true,
  market_research: true,
  customer_research: true,
};

const defaultProfile: UserProfile = {
  name: null,
  company: null,
  role: null,
};

export const useAuth = (): AuthState & { refreshCredits: () => Promise<void>; refreshProfile: () => Promise<void> } => {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { user } = useUser();
  const [credits, setCredits] = useState<number>(0);
  const [agentAccess, setAgentAccess] = useState<AgentAccess>(defaultAgentAccess);
  const [profile, setProfile] = useState<UserProfile>(defaultProfile);

  const resolveToken = useCallback(async (options?: { skipCache?: boolean }): Promise<string | null> => {
    const direct = await getToken(options);
    if (direct) {
      return direct;
    }
    if (!options?.skipCache) {
      await new Promise((resolve) => {
        setTimeout(resolve, 250);
      });
      const retry = await getToken();
      return retry || null;
    }
    return null;
  }, [getToken]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    if (!isSignedIn) {
      apiService.setTokenProvider(null);
      setCredits(0);
      setAgentAccess(defaultAgentAccess);
      setProfile(defaultProfile);
      return;
    }

    apiService.setTokenProvider(async (options) => {
      try {
        return await resolveToken(options);
      } catch {
        return null;
      }
    });

    const syncSession = async () => {
      try {
        const [balanceResult, accessResult, profileResult] = await Promise.allSettled([
          apiService.getCreditsBalance(),
          apiService.getAgentAccess(),
          apiService.getUserProfile()
        ]);

        if (balanceResult.status === 'fulfilled') {
          setCredits(Number.isFinite(balanceResult.value.balance) ? balanceResult.value.balance : 0);
        } else if (!isAbortError(balanceResult.reason)) {
          console.warn('Credits sync temporarily unavailable:', balanceResult.reason);
        }

        if (accessResult.status === 'fulfilled') {
          setAgentAccess(accessResult.value);
        } else {
          console.error('Failed to sync agent access:', accessResult.reason);
        }

        if (profileResult.status === 'fulfilled') {
          setProfile(profileResult.value);
        } else {
          console.error('Failed to sync user profile:', profileResult.reason);
        }
      } catch (error) {
        console.error('Failed to sync session:', error);
      }
    };

    syncSession();

    // Proactive token refresh every 50 seconds (before 60s expiry)
    const refreshInterval = setInterval(async () => {
      try {
        await getToken({ skipCache: true });
      } catch (err) {
        console.error('[Auth] Proactive token refresh failed:', err);
      }
    }, 50000);

    return () => clearInterval(refreshInterval);
  }, [isLoaded, isSignedIn, resolveToken, user?.id, getToken]);

  const refreshCredits = useCallback(async () => {
    if (!isSignedIn) {
      setCredits(0);
      return;
    }
    
    try {
      const balance = await apiService.getCreditsBalance();
      setCredits(Number.isFinite(balance.balance) ? balance.balance : 0);
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      console.error('Failed to refresh credits:', error);
    }
  }, [isSignedIn]);

  const refreshProfile = useCallback(async () => {
    if (!isSignedIn) {
      setProfile(defaultProfile);
      return;
    }
    
    try {
      const userProfile = await apiService.getUserProfile();
      setProfile(userProfile);
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  }, [isSignedIn]);

  return {
    isLoaded,
    isSignedIn: isSignedIn ?? false,
    userId: user?.id ?? null,
    credits,
    agentAccess,
    profile,
    refreshCredits,
    refreshProfile
  };
};
